// El lienzo de `/agente` — ADR-2026-09-16-el-lienzo-del-agente-es-react-flow.
//
// React Flow con la misma configuración que Flujos (`FlujoEditor.tsx`): pan con scroll,
// zoom con cmd/ctrl + rueda, arrastre de nodos, selección por área, fondo punteado que
// escala. Sin conexiones ni herramientas: acá sólo hay fotos sueltas
// (ADR-2026-09-16-las-fotos-del-lienzo-van-sueltas). Es una COLUMNA inline de la
// pantalla, no un overlay: no va por portal.
//
// Los nodos salen del acomodo (tandas de los mensajes + posiciones guardadas +
// proporciones reales de las fotos). React Flow necesita su propia copia para animar el
// arrastre, así que se le entregan con `setNodes` cada vez que cambian los calculados,
// conservando qué estaba seleccionado, y al soltar se guarda la posición.
//
// EL ENCUADRE espera a que React Flow haya MEDIDO los nodos (`useNodesInitialized`):
// encuadrar antes deja el viewport en la esquina, que es lo que pasaba con la primera
// foto. Y espera además a que termine de abrirse la columna (su ancho se anima desde
// cero): encuadrar contra una columna a medio abrir también sale torcido. Lo mismo con
// cada tanda NUEVA: sus celdas llegan al store de React Flow un render después de que
// la tanda aparece, y pedir `fitView` por ids que el store no tiene encuadra una caja
// vacía en el origen —el lienzo "se iba a otro lado"—. El pedido se guarda hasta que
// todas las celdas están (`encuadre.ts`,
// ADR-2026-10-05-el-encuadre-espera-a-que-react-flow-tenga-las-celdas).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  SelectionMode,
  applyNodeChanges,
  useNodesInitialized,
  useReactFlow,
  type Node,
  type NodeChange,
  type NodeTypes,
  type OnBeforeDelete,
  type OnNodeDrag,
  type OnSelectionChangeParams,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
// Las herramientas de Flujos, tal cual (ADR-2026-09-16-el-lienzo-toma-la-barra-de-flujos):
// la barra, la capa de dibujo, los nodos de texto/nota/comentario y su contexto.
import { accionDeTecla } from '../../pages/flujos/graph/atajos';
import {
  agregarMensaje as agregarMensajeHilo,
  alternarResuelto as alternarResueltoHilo,
  normalizarHilo,
} from '../../pages/flujos/graph/comentarios';
import type { LucideIcon } from 'lucide-react';
import { COLORES_TRAZO } from '../../pages/flujos/graph/trazos';
import type { Trazo } from '../../pages/flujos/graph/types';
import { NodoComentario } from '../../pages/flujos/nodes/NodoComentario';
import { NodoNota } from '../../pages/flujos/nodes/NodoNota';
import { NodoTexto } from '../../pages/flujos/nodes/NodoTexto';
import { BarraHerramientas, type Herramienta as HerramientaDeBarra } from '../../pages/flujos/panels/BarraHerramientas';
import { CapaDibujo, type EstadoDibujo } from '../../pages/flujos/panels/CapaDibujo';
import { ComentariosContext, type ComentariosCtx } from '../../pages/flujos/panels/comentariosContext';
import '../../pages/flujos/flujos.css';
import { createPortal } from 'react-dom';
import { AlertCircle } from 'lucide-react';
import { useOpcionesDeDescarga } from '../../components/descarga/useOpcionesDeDescarga';
import { useAnalisisAutomatico, useVerificacionCalidad } from '../../hooks/useFuncionesAvanzadas';
import { useTiendaConectada } from '../../hooks/useTiendaConectada';
import {
  GeneratedImagesHistory,
  type AccionVisor,
  type FilaInicialVisor,
} from '../../pages/GeneratedImagesHistory';
import { useAgentEnv } from '../../pages/v2/agentEnvContext';
import { filaInicialDeCelda, precargarOriginal } from '../../pages/v2/components/filaInicialDelVisor';
import { ordenDeLectura } from './ordenDelVisor';
import { registrarVisorDelLienzo } from '../../pages/v2/visorDelLienzo';
import type { ChatAttachment } from '../../pages/v2/agentAttachments';
import type { GeneracionDelegada } from '../../pages/v2/components/generacionDelegada';
import { useVerificacion } from '../../pages/v2/verificacion/useVerificacion';
import { GenerateAdError } from '../../lib/generateAdRequest';
import { preloadGeneratedImages } from '../../utils/preloadGeneratedImages';
import { preloadVideosDeTanda } from '../../utils/preloadVideosDeTanda';
import { ratioDeFormato } from '../../utils/ratioDeFormato';
import { useChatAtlas } from '../ChatAtlasContext';
import { useEntornoChatAtlas } from '../entornoContext';
import { SIN_SELECCION } from '../seleccionDelLienzo';
import { adjuntoDeCelda, contarAccionables, type CeldaParaAccion } from './accionesDeCelda';
import { anotacionesDeNodos, esAnotacion, nodoDeAnotacion, nuevaAnotacion } from './anotaciones';
import { acomodarLienzo, lugarDeTanda, posicionesSueltas, type Posiciones, type Ratios, ANCHO_CELDA } from './acomodo';
import { BarraDeSeleccion } from './BarraDeSeleccion';
import { celdasParaEncuadrar, tandasSinVer } from './encuadre';
import { ordenarEnGrilla } from './ordenarSeleccion';

/** Lo que dura el viaje de las celdas al ordenarlas (igual que la transición de `lienzo.css`). */
const MS_ORDENAR = 450;
import type { Herramienta, PedidoDeHerramienta } from './herramientas/herramienta';
import { PanelDeHerramienta } from './herramientas/PanelDeHerramienta';
import { LienzoContext, type PropsDeBarraDeCelda, type ValorLienzo } from './lienzoContext';
import { NodoCelda } from './NodoCelda';
import { recortarTitulo } from './tandasDelLienzo';
import type { LienzoDelChat } from './useLienzoDelChat';
import { useMiniaturasYBorradas } from './useMiniaturasDelLienzo';
import { sinBorradas } from './sinBorradas';
import { useCreacionesEliminadas } from '../../lib/creacionesEliminadas';
import { idsDeTandasDeSesion, idsVerificables, tandasEnVuelo } from './verificacionDelLienzo';
import './lienzo.css';

// Los nodos de Flujos vienen tipados con su `FlowNode`; acá leen lo mismo (`data.params`).
const NODE_TYPES = {
  celda: NodoCelda,
  texto: NodoTexto,
  nota: NodoNota,
  comentario: NodoComentario,
} as unknown as NodeTypes;

/** La tabla del popover de ayuda: lo que hay en ESTE canvas (sin puertos ni cables). */
const ATAJOS_DEL_LIENZO: Array<[string, string]> = [
  ['V · H · T · C · P · N', 'Seleccionar, mano, texto, comentar, dibujar, nota'],
  ['Click en una foto', 'Sus acciones; otro click, la vista ampliada'],
  ['Arrastrar en el fondo', 'Seleccionar varias'],
  ['Supr', 'Borrar textos, notas y comentarios'],
  ['Scroll · ⌘/Ctrl + scroll', 'Mover el canvas · zoom'],
  ['Esc', 'Salir de la herramienta y deseleccionar'],
  ['?', 'Esta ayuda'],
];

const paramsDe = (n: Node): Record<string, unknown> =>
  ((n.data as { params?: Record<string, unknown> }).params ?? {});

/** Hasta dónde acerca el encuadre: una foto sola se ve grande, no a tamaño de celda. */
const ZOOM_MAXIMO_ENCUADRE = 1.6;

interface Props {
  estado: LienzoDelChat;
  /** Ancho fijo de la columna: el contenedor de afuera anima el suyo desde cero y este
   *  se queda a su medida, así el contenido no se aplasta mientras se abre. */
  ancho: number;
  className?: string;
  /**
   * Lo de adentro se muestra (con un fundido) y se encuadra recién cuando quien monta
   * el lienzo dice que su columna terminó de abrirse
   * (ADR-2026-09-17-el-lienzo-abre-por-etapas): encuadrar contra una columna a medio
   * abrir salía torcido, y las cards apareciendo mientras la columna crece se veían a
   * los tirones. Por defecto `true`: sin coreografía, se muestra al montar.
   */
  contenidoVisible?: boolean;
  /**
   * Las tres ranuras que presta el lienzo (ADR-2026-09-17-el-lienzo-presta-tres-ranuras):
   * otra barra por celda, un adorno encima de cada foto y un interceptor del click.
   * Sin ellas, el lienzo del Agente: `BarraDeCelda`, nada, y el click abre el visor.
   */
  Barra?: React.ComponentType<PropsDeBarraDeCelda>;
  Adorno?: React.ComponentType<{ id: string; ratio: number }>;
  /** Devuelve `true` si se quedó con el click (no se abre el visor). */
  alTocarCelda?: (id: string) => boolean;
  /** Devuelve `true` si esa celda no muestra su pie (el título) por ahora (ver `ValorLienzo.sinPie`). */
  sinPie?: (id: string) => boolean;
  /** Una capa adentro de React Flow, en las coordenadas del lienzo (ver `ValorLienzo.Capa`). */
  Capa?: React.ComponentType;
  /**
   * El chip de una imagen PROPIA del modo (una celda sin fila, con `url`), para que al
   * seleccionarla aparezca apagada en la caja del chat como las creaciones
   * (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir). Sin esto, las
   * propias seleccionadas no van a la caja.
   */
  adjuntoDePropia?: (id: string) => ChatAttachment | null;
  /**
   * Una herramienta del MODO que, mientras está, reemplaza en la barra a Texto, Comentar
   * y Dibujar (ADR-2026-09-21-el-marcador-de-la-mezcla). Prendida, convive con
   * Seleccionar; elegir otra herramienta del canvas o Esc la apaga.
   */
  herramientaDelModo?: HerramientaDelModo | null;
  /**
   * Dónde va la barra de herramientas del canvas: `'abajo'` (default, al centro) o
   * `'derecha'` (una columna a media altura). El modo Redes la pide a la derecha porque
   * su caja de prompt ocupa el pie del lienzo (ADR-2026-10-01-modo-redes).
   */
  barra?: 'abajo' | 'derecha';
  /**
   * Qué hace "Agregar" con una foto del lienzo, cuando NO es mandarla al chat. El modo
   * Redes la pone de referencia en su caja (`agregar`) y el botón dice sólo "Agregar"
   * (`rotulo`) — ADR-2026-10-04-agregar-del-lienzo-va-a-la-caja-de-redes. Sin esto, el
   * comportamiento de siempre: al chat, abriendo el panel si estaba cerrado.
   */
  alAgregar?: {
    rotulo: string;
    agregar: (adjunto: ChatAttachment, origen?: { rect: DOMRect; src: string }) => void;
  };
}

export interface HerramientaDelModo {
  rotulo: string;
  Icono: LucideIcon;
  activa: boolean;
  onCambiar: (activa: boolean) => void;
}

const Lienzo: React.FC<Props> = ({
  estado,
  ancho,
  className = '',
  contenidoVisible = true,
  Barra,
  Adorno,
  alTocarCelda,
  sinPie,
  Capa,
  adjuntoDePropia,
  herramientaDelModo = null,
  barra = 'abajo',
  alAgregar,
}) => {
  const chat = useChatAtlas();
  const { companyId, brandId } = useAgentEnv();
  const tiendaConectada = useTiendaConectada();
  const { chooserElement, resolverOpciones, aplicarOpciones } = useOpcionesDeDescarga();
  const { tandas: tandasGuardadas, lienzo, moverNodos, agregarTanda, fijarColocador, setAnotaciones, setTrazos: guardarTrazos, versionCarga } = estado;
  const rf = useReactFlow();
  const inicializados = useNodesInitialized();
  const { usuarioId, shortName } = useEntornoChatAtlas();
  const usuario = useMemo(() => ({ id: usuarioId ?? '', nombre: shortName || 'Alguien' }), [usuarioId, shortName]);

  // ── Las herramientas de la barra (ADR-2026-09-16-el-lienzo-toma-la-barra-de-flujos).
  const [herramienta, setHerramienta] = useState<HerramientaDeBarra>('seleccionar');
  const [estadoDibujo, setEstadoDibujo] = useState<EstadoDibujo>({
    color: COLORES_TRAZO[0],
    ancho: 4,
    borrador: false,
    autoresOcultos: [],
  });
  const [ayudaAbierta, setAyudaAbierta] = useState(false);
  const [trazos, setTrazos] = useState<Trazo[]>([]);
  const [abiertoId, setAbiertoId] = useState<string | null>(null);
  const herramientaDelModoRef = useRef(herramientaDelModo);
  herramientaDelModoRef.current = herramientaDelModo;
  // Si aparece la del modo mientras estaba elegida una de las que tapa, se vuelve a Seleccionar.
  useEffect(() => {
    if (herramientaDelModo) setHerramienta((h) => (h === 'texto' || h === 'comentar' || h === 'dibujar' ? 'seleccionar' : h));
  }, [herramientaDelModo]);
  const cambiarHerramienta = useCallback((h: HerramientaDeBarra) => {
    // Las tres que la herramienta del modo reemplaza no existen mientras ella está.
    if (herramientaDelModoRef.current && (h === 'texto' || h === 'comentar' || h === 'dibujar')) return;
    // Otra herramienta del canvas (o Esc, que vuelve a Seleccionar) apaga la del modo.
    if (herramientaDelModoRef.current?.activa) herramientaDelModoRef.current.onCambiar(false);
    setHerramienta(h);
    if (h !== 'dibujar') setEstadoDibujo((e) => ({ ...e, borrador: false }));
    // Con otra herramienta no queda nada seleccionado: la barra de una foto se va, y un
    // click sobre ella con Texto/Nota/Comentar crea la anotación en vez de abrir el visor.
    if (h !== 'seleccionar') {
      setNodes((nds) => (nds.some((n) => n.selected) ? nds.map((n) => ({ ...n, selected: false })) : nds));
    }
  }, []);

  // Lo guardado puede nombrar creaciones que ya se ELIMINARON (en esta sesión, o antes:
  // la fila dice `active = false`). Se sacan acá, antes del acomodo, y todo lo de abajo
  // trabaja con `tandas` ya limpias; si no, quedaban como un recuadro gris
  // (ADR-2026-10-04-eliminar-saca-la-creacion-de-la-vista).
  const eliminadasEnSesion = useCreacionesEliminadas();
  const idsGuardados = useMemo(
    () =>
      tandasGuardadas.flatMap((t) => t.celdas.map((c) => c.imageId)).filter((x): x is string => !!x),
    [tandasGuardadas],
  );
  const { miniaturas, borradas } = useMiniaturasYBorradas(idsGuardados, companyId);
  const tandas = useMemo(
    () => sinBorradas(tandasGuardadas, (id) => eliminadasEnSesion.has(id) || borradas.has(id)),
    [tandasGuardadas, eliminadasEnSesion, borradas],
  );
  const ids = useMemo(
    () =>
      tandas.flatMap((t) => t.celdas.map((c) => c.imageId)).filter((x): x is string => !!x),
    [tandas],
  );

  // Proporciones reales: primero la que dice la fila (`formato`, que en un video es una
  // apuesta), y encima la medida de la imagen cuando cargó, que es la verdad.
  const [ratiosMedidos, setRatiosMedidos] = useState<Ratios>({});
  const reportarRatio = useCallback((imageId: string, ratio: number) => {
    setRatiosMedidos((prev) =>
      Math.abs((prev[imageId] ?? 0) - ratio) < 0.01 ? prev : { ...prev, [imageId]: ratio },
    );
  }, []);
  const ratios = useMemo<Ratios>(() => {
    const out: Ratios = {};
    for (const id of ids) {
      const r = ratioDeFormato(miniaturas.get(id)?.datos?.formato);
      if (r) out[id] = r;
    }
    return { ...out, ...ratiosMedidos };
  }, [ids, miniaturas, ratiosMedidos]);

  const nodos = useMemo(
    () => acomodarLienzo(tandas, lienzo.posiciones, ratios),
    [tandas, lienzo.posiciones, ratios],
  );
  // Para decidir dónde aterriza una tanda nueva en el momento en que se la pide, sin
  // que los callbacks que la piden cambien con cada acomodo.
  const nodosRef = useRef(nodos);
  nodosRef.current = nodos;
  // Quien pide una tanda sin ver los nodos (el modo Diseño) igual la deja fija donde cae
  // (ADR-2026-09-18-las-fotos-no-se-mueven-juntas).
  useEffect(() => {
    fijarColocador((origenId, total) => lugarDeTanda(nodosRef.current, origenId, total));
    return () => fijarColocador(null);
  }, [fijarColocador]);
  const posicionesRef = useRef(lienzo.posiciones);
  posicionesRef.current = lienzo.posiciones;

  // Los nodos: las celdas (derivadas del acomodo, se rehacen enteras) y las anotaciones
  // (viven acá, en el estado de React Flow, y se suben al chat cuando cambian).
  const [nodes, setNodes] = useState<Node[]>([]);
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  useEffect(() => {
    setNodes((prev) => {
      const previos = new Map(prev.map((n) => [n.id, n]));
      // Las celdas se rehacen enteras, así que hay que CONSERVAR la medida que React Flow
      // ya tomó: un nodo nuevo sin `measured` queda `visibility: hidden` hasta que su
      // ResizeObserver dispare, y si el tamaño no cambió no dispara nunca — el lienzo se
      // quedaba con las fotos invisibles (sólo los rótulos, que van en otra capa) hasta
      // recargar. Y `width`/`height` del acomodo como medida inicial, por si es la
      // primera vez (ADR-2026-09-17-las-celdas-conservan-su-medida).
      const celdas = (nodos as unknown as Node[]).map((n) => {
        const previo = previos.get(n.id);
        const style = n.style as { width: number; height: number };
        return {
          ...n,
          width: style.width,
          height: style.height,
          ...(previo?.measured ? { measured: previo.measured } : {}),
          selected: previo?.selected ?? false,
        };
      });
      return [...celdas, ...prev.filter(esAnotacion)];
    });
  }, [nodos]);
  // Lo guardado entra al estado cuando se lee (otro chat, o el mismo al volver). Se anota
  // qué versión se cargó: hasta que los nodos reflejen la nueva, no se sube nada al chat
  // —si no, las anotaciones del chat anterior se guardarían en el nuevo.
  const [versionEnNodos, setVersionEnNodos] = useState(-1);
  useEffect(() => {
    setNodes((prev) => [...prev.filter((n) => !esAnotacion(n)), ...lienzo.anotaciones.map((a) => nodoDeAnotacion(a))]);
    setTrazos(lienzo.trazos);
    setAbiertoId(null);
    setVersionEnNodos(versionCarga);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionCarga]);
  useEffect(() => {
    if (versionEnNodos !== versionCarga) return;
    setAnotaciones(anotacionesDeNodos(nodes));
  }, [nodes, versionEnNodos, versionCarga, setAnotaciones]);
  useEffect(() => {
    if (versionEnNodos !== versionCarga) return;
    guardarTrazos(trazos);
  }, [trazos, versionEnNodos, versionCarga, guardarTrazos]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => setNodes((ns) => applyNodeChanges(changes, ns)),
    [],
  );
  // Supr borra sólo anotaciones: una foto del chat no se borra desde el lienzo.
  const onBeforeDelete = useCallback<OnBeforeDelete>(
    async ({ nodes: ns }) => ({ nodes: ns.filter(esAnotacion), edges: [] }),
    [],
  );
  // Lo que todavía está donde el acomodo lo puso se guarda tal cual junto con lo movido:
  // si no, al guardar lo movido el acomodo recalculaba lo demás a partir de ello y las
  // fotos que nunca tocaron se iban detrás, como vinculadas
  // (ADR-2026-09-18-las-fotos-no-se-mueven-juntas).
  const congelarSueltas = useCallback(
    (nuevas: Posiciones): Posiciones => ({
      ...posicionesSueltas(nodesRef.current.filter((n) => !esAnotacion(n)), posicionesRef.current),
      ...nuevas,
    }),
    [],
  );
  // Al soltar se guarda lo movido: el nodo arrastrado y los que iban seleccionados con él.
  // Las anotaciones guardan su posición en sí mismas, no en `posiciones`.
  const onNodeDragStop = useCallback<OnNodeDrag>(
    (_, nodo, arrastrados) => {
      const nuevas: Posiciones = {};
      for (const n of arrastrados.length > 0 ? arrastrados : [nodo]) {
        if (!esAnotacion(n)) nuevas[n.id] = { x: n.position.x, y: n.position.y };
      }
      if (Object.keys(nuevas).length > 0) moverNodos(congelarSueltas(nuevas));
    },
    [moverNodos, congelarSueltas],
  );

  // ── Comentarios: el mismo contrato que Flujos le da a `NodoComentario`.
  const abiertoRef = useRef(abiertoId);
  abiertoRef.current = abiertoId;
  const cerrarHiloAbierto = useCallback(() => {
    const id = abiertoRef.current;
    if (!id) return;
    // Se abrió y no se escribió nada: no queda un pin vacío.
    const n = nodesRef.current.find((x) => x.id === id);
    if (n && normalizarHilo(paramsDe(n)).hilo.length === 0) void rf.deleteElements({ nodes: [{ id }] });
    setAbiertoId(null);
  }, [rf]);
  const comentariosCtx = useMemo<ComentariosCtx>(
    () => ({
      usuario,
      participantes: [{ id: usuario.id, nombre: usuario.nombre, ultimaVez: '' }],
      abiertoId,
      abrir: (id) => setAbiertoId(id),
      agregarMensaje: (id, texto) =>
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? {
                  ...n,
                  data: {
                    ...n.data,
                    params: agregarMensajeHilo(paramsDe(n), { autorId: usuario.id, autorNombre: usuario.nombre, texto }),
                  },
                }
              : n,
          ),
        ),
      alternarResuelto: (id) =>
        setNodes((nds) =>
          nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, params: alternarResueltoHilo(paramsDe(n)) } } : n)),
        ),
      borrar: (id) => {
        setAbiertoId((a) => (a === id ? null : a));
        void rf.deleteElements({ nodes: [{ id }] });
      },
      ocultarResueltos: false,
    }),
    [usuario, abiertoId, rf],
  );
  // El pin abierto va por encima de todo lo demás.
  useEffect(() => {
    setNodes((nds) =>
      nds.some((n) => n.type === 'comentario')
        ? nds.map((n) => (n.type === 'comentario' ? { ...n, zIndex: n.id === abiertoId ? 2000 : 500 } : n))
        : nds,
    );
  }, [abiertoId]);

  // ── Texto, nota y comentario nacen donde se toca el fondo, como en Flujos.
  const onPaneClick = useCallback(
    (e: React.MouseEvent) => {
      if (herramienta === 'texto' || herramienta === 'nota' || herramienta === 'comentar') {
        const pos = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const tipo = herramienta === 'comentar' ? 'comentario' : herramienta;
        if (tipo === 'comentario') cerrarHiloAbierto();
        const a = nuevaAnotacion(tipo, pos, usuario);
        setNodes((nds) => [...nds.map((n) => ({ ...n, selected: false })), nodoDeAnotacion(a, tipo !== 'comentario')]);
        if (tipo === 'comentario') setAbiertoId(a.id);
        cambiarHerramienta('seleccionar');
        return;
      }
      cerrarHiloAbierto();
    },
    [herramienta, rf, usuario, cerrarHiloAbierto, cambiarHerramienta],
  );

  // ── Atajos: los mismos de Flujos, sin las acciones de flujo (correr, duplicar, deshacer).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const accion = accionDeTecla({
        key: e.key,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        shiftKey: e.shiftKey,
        tag: el?.tagName ?? 'DIV',
        editable: !!el?.isContentEditable,
      });
      if (!accion) return;
      if (accion.startsWith('tool:')) {
        e.preventDefault();
        cambiarHerramienta(accion.slice(5) as HerramientaDeBarra);
        return;
      }
      if (accion === 'ayuda') {
        e.preventDefault();
        setAyudaAbierta((v) => !v);
        return;
      }
      if (accion === 'deseleccionar') {
        setAyudaAbierta(false);
        cambiarHerramienta('seleccionar');
        cerrarHiloAbierto();
        if (el && el !== document.body) el.blur();
        setNodes((nds) => (nds.some((n) => n.selected) ? nds.map((n) => ({ ...n, selected: false })) : nds));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cambiarHerramienta, cerrarHiloAbierto]);

  const [seleccionIds, setSeleccionIds] = useState<string[]>([]);
  const onSelectionChange = useCallback(
    ({ nodes: sel }: OnSelectionChangeParams) => setSeleccionIds(sel.map((n) => n.id)),
    [],
  );
  const seleccionadas = useMemo<CeldaParaAccion[]>(() => {
    if (!companyId) return [];
    const out: CeldaParaAccion[] = [];
    for (const n of nodos) {
      if (!n.data.imageId || !seleccionIds.includes(n.id)) continue;
      const m = miniaturas.get(n.data.imageId);
      if (!m?.datos) continue;
      out.push({
        imageId: n.data.imageId,
        companyId,
        esVideo: n.data.esVideo,
        src: m.src,
        datos: m.datos,
        titulo: n.data.titulo,
      });
    }
    return out;
  }, [nodos, seleccionIds, miniaturas, companyId]);
  // Cuántas de las seleccionadas muestran barra. `seleccionadas` no sirve para eso: no
  // cuenta las imágenes propias del modo Diseño (no tienen fila), así que su barra nunca
  // aparecía (ADR-2026-09-17-la-barra-cuenta-lo-accionable). Sin barra prestada esto da
  // exactamente `seleccionadas.length`, o sea `/agente` queda igual que antes.
  const seleccionAccionable = useMemo(
    () => contarAccionables(nodos, seleccionIds, (id) => !!miniaturas.get(id)?.datos, !!Barra),
    [nodos, seleccionIds, miniaturas, Barra],
  );
  // Lo seleccionado, informado al chat: aparece apagado en la caja y se adjunta al
  // escribir (ADR-2026-09-24-la-seleccion-del-lienzo-se-adjunta-al-escribir). El chip de
  // cada celda es el mismo que arma "Agregar al chat"; una propia del modo, el que preste
  // `adjuntoDePropia`. En el orden de la selección, sin anotaciones.
  const chipsDeSeleccion = useMemo<ChatAttachment[]>(() => {
    const porCelda = new Map(seleccionadas.map((c) => [c.imageId, c] as const));
    const out: ChatAttachment[] = [];
    for (const id of seleccionIds) {
      const n = nodos.find((x) => x.id === id);
      if (!n) continue;
      const chip = n.data.imageId
        ? (() => {
            const c = porCelda.get(n.data.imageId);
            return c ? adjuntoDeCelda(c) : null;
          })()
        : n.data.url
          ? (adjuntoDePropia?.(id) ?? null)
          : null;
      if (chip) out.push(chip);
    }
    return out;
  }, [seleccionIds, nodos, seleccionadas, adjuntoDePropia]);
  const fijarSeleccionDelLienzo = chat.fijarSeleccionDelLienzo;
  useEffect(() => {
    fijarSeleccionDelLienzo({ ids: seleccionIds, chips: chipsDeSeleccion });
  }, [fijarSeleccionDelLienzo, seleccionIds, chipsDeSeleccion]);
  useEffect(() => () => fijarSeleccionDelLienzo(SIN_SELECCION), [fijarSeleccionDelLienzo]);

  // ORDENAR la selección en grilla (ADR-2026-09-17-la-seleccion-se-ordena-en-grilla): las
  // posiciones nuevas se guardan como si las hubieran arrastrado, y mientras dura el
  // viaje el lienzo lleva `lienzo-ordenando`, que le pone transición al `transform` de
  // los nodos (`lienzo.css`). Sólo las celdas: las anotaciones no entran en la grilla.
  const [ordenando, setOrdenando] = useState(false);
  const ordenarSeleccion = useCallback(() => {
    const celdas = nodesRef.current
      .filter((n) => !esAnotacion(n) && seleccionIds.includes(n.id))
      .map((n) => ({
        id: n.id,
        position: n.position,
        ancho: Number(n.measured?.width ?? n.style?.width) || ANCHO_CELDA,
        alto: Number(n.measured?.height ?? n.style?.height) || ANCHO_CELDA,
      }));
    if (celdas.length < 2) return;
    const nuevas = ordenarEnGrilla(celdas);
    setOrdenando(true);
    setNodes((nds) => nds.map((n) => (nuevas[n.id] ? { ...n, position: nuevas[n.id] } : n)));
    moverNodos(congelarSueltas(nuevas));
    window.setTimeout(() => setOrdenando(false), MS_ORDENAR);
  }, [seleccionIds, moverNodos, congelarSueltas]);

  // ENCUADRE. Primera vez: todo, cuando los nodos están medidos y la columna abierta.
  // Después: cada tanda nueva se encuadra sola, con animación, apenas React Flow tiene
  // sus celdas. Si llegan varias juntas, se encuadran juntas.
  const primeraVistaRef = useRef(false);
  const vistasRef = useRef<Set<string>>(new Set());
  const pendientesRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!primeraVistaRef.current) return;
    for (const id of tandasSinVer(tandas, vistasRef.current)) {
      vistasRef.current.add(id);
      pendientesRef.current.add(id);
    }
  }, [tandas]);
  useEffect(() => {
    if (!inicializados || nodes.length === 0 || !contenidoVisible) return;
    if (!primeraVistaRef.current) {
      primeraVistaRef.current = true;
      for (const t of tandas) vistasRef.current.add(t.id);
      void rf.fitView({ padding: 0.25, maxZoom: ZOOM_MAXIMO_ENCUADRE });
      return;
    }
    if (pendientesRef.current.size === 0) return;
    // Las celdas nuevas entran al store de React Flow con `setNodes`, un render después
    // de aparecer en `nodos`: hasta que estén TODAS, el pedido queda y se vuelve a mirar
    // cuando `nodes` cambie. Pedir `fitView` por ids que el store no tiene encuadra una
    // caja vacía en el origen (`encuadre.ts`).
    const celdas = celdasParaEncuadrar(nodos, [...pendientesRef.current], (id) => !!rf.getInternalNode(id));
    if (celdas === null) return;
    pendientesRef.current.clear();
    if (celdas.length === 0) return;
    // `includeHiddenNodes`: que cuenten por su `width`/`height` declarados (los del
    // acomodo, exactos) y no sólo por la medida del ResizeObserver, que puede llegar
    // después. No hay nodos ocultos en este lienzo.
    void rf.fitView({
      nodes: celdas,
      includeHiddenNodes: true,
      duration: 400,
      maxZoom: ZOOM_MAXIMO_ENCUADRE,
      padding: 0.3,
    });
  }, [inicializados, nodes, nodos, tandas, rf, contenidoVisible]);

  const [abierta, setAbierta] = useState<{
    imageId: string;
    accion?: AccionVisor;
    fila?: FilaInicialVisor;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // La herramienta abierta sobre una celda, con panel propio (sin visor).
  const [pedido, setPedido] = useState<PedidoDeHerramienta | null>(null);
  const abrirHerramienta = useCallback(
    (celda: CeldaParaAccion, herramienta: Herramienta) =>
      setPedido({ celda, herramienta, ratio: ratios[celda.imageId] ?? 0.8 }),
    [ratios],
  );
  const cerrarHerramienta = useCallback(() => setPedido(null), []);

  // Lo que el visor genera sobre una foto del lienzo aterriza acá, debajo de esa foto
  // (ADR-2026-09-16-las-tandas-del-visor-aterrizan-en-el-lienzo). Carrusel y Animar
  // llegan como promesa de ids: se espera el preload (las filas pueden no tener archivo
  // todavía, sobre todo el video y su thumbnail) antes de cerrar la tanda, igual que la
  // columna de resultados. La Expansión llega ya guardada y firmada: tanda de una foto.
  const alDelegar = useCallback(
    (g: GeneracionDelegada) => {
      if (!companyId || !g.origenId) return;
      const listas = g.resultado.then(async (ids) => {
        try {
          const pre = g.esVideo
            ? await preloadVideosDeTanda(ids, companyId)
            : await preloadGeneratedImages(ids, companyId);
          if (pre.length > 0) return pre.map((p) => String(p.id));
        } catch {
          // Sin preload, los ids igual sirven: la miniatura se pide por su cuenta.
        }
        return ids;
      });
      agregarTanda({
        titulo: recortarTitulo(
          g.producto || (g.esVideo ? 'Video' : g.tipo === 'regenerar' ? 'Regenerada' : g.tipo === 'editar' ? 'Edición' : 'Carrusel'),
        ),
        total: g.total,
        ratio: g.ratio,
        esVideo: g.esVideo,
        origenId: g.origenId,
        // El lugar se decide ahora y viaja guardado: la tanda no sigue a la foto
        // (ADR-2026-09-17-la-tanda-aterriza-una-vez).
        posicion: lugarDeTanda(nodosRef.current, g.origenId, g.total),
        // Con execution ids la tanda se retoma tras una recarga (Pasar a 1080p los manda).
        ...(g.executionIds && g.executionIds.length > 0 ? { executionIds: g.executionIds } : {}),
        resultado: listas,
      });
      g.resultado.catch((e: unknown) => {
        setError(
          e instanceof GenerateAdError && e.isActionable
            ? e.message
            : g.tipo === 'pasar-a-1080p'
              ? 'No se pudo pasar el video a 1080p. Intentá nuevamente.'
              : g.esVideo
              ? 'No se pudo generar el video. Intentá nuevamente.'
              : g.tipo === 'regenerar'
                ? 'No se pudo regenerar la imagen. Intentá nuevamente.'
                : g.tipo === 'editar'
                  ? 'No se pudo editar la imagen. Intentá nuevamente.'
                  : 'No se pudo generar nuevos ángulos. Intentá nuevamente.',
        );
      });
    },
    [companyId, agregarTanda],
  );
  const alExpandir = useCallback(
    (img: { id: string; origenId: string; ratio: number; producto: string; esVideo?: boolean }) =>
      agregarTanda({
        titulo: recortarTitulo(img.producto || (img.esVideo ? 'Video' : 'Expansión')),
        total: 1,
        ratio: img.ratio,
        esVideo: !!img.esVideo,
        origenId: img.origenId,
        posicion: lugarDeTanda(nodosRef.current, img.origenId, 1),
        resultado: Promise.resolve([img.id]),
      }),
    [agregarTanda],
  );

  // Al tocar se le da al visor la fila que el lienzo ya tiene (miniatura decodificada,
  // firma del original) y se arranca la descarga del original antes de que monte: es lo
  // que hace Creaciones, y sin esto el visor encadenaba dos viajes antes de dibujar
  // (ADR-2026-09-16-el-lienzo-abre-el-visor-con-la-fila-que-ya-tiene).
  const filaDeCelda = useCallback(
    (imageId: string): FilaInicialVisor | undefined => {
      const m = miniaturas.get(imageId);
      return m ? filaInicialDeCelda({ id: imageId, src: m.src, datos: m.datos }, companyId) : undefined;
    },
    [miniaturas, companyId],
  );
  const abrir = useCallback(
    (imageId: string, accion?: AccionVisor) => {
      const fila = filaDeCelda(imageId);
      if (fila) precargarOriginal(fila, companyId, miniaturas.get(imageId)?.datos?.tipo === 'video');
      setAbierta({ imageId, accion, fila });
    },
    [filaDeCelda, miniaturas, companyId],
  );
  // Las flechas del visor recorren el lienzo como se lee —filas de arriba a abajo, cada una
  // de izquierda a derecha— y no el historial por fecha, que iba al revés de como caen las
  // tandas (ADR-2026-10-23-las-flechas-del-visor-siguen-al-lienzo-y-al-chat).
  const ordenDelVisor = useMemo(
    () =>
      ordenDeLectura(
        nodos.map((n) => ({ imageId: n.data.imageId, position: n.position, alto: n.style.height })),
      ),
    [nodos],
  );
  // Mientras el lienzo está a la vista, tocar una imagen en una card del chat abre ESTE
  // visor (ADR-2026-09-18-la-foto-del-chat-se-abre-en-el-visor-del-lienzo).
  useEffect(() => {
    registrarVisorDelLienzo((imageId) => abrir(imageId));
    return () => registrarVisorDelLienzo(null);
  }, [abrir]);
  // "Agregar al chat" desde el lienzo abre el panel lateral si estaba cerrado: si no, el
  // chip quedaba enganchado en un chat que no se veía (pedido de Matías del 2026-09-18).
  // En la pantalla dedicada el chat ya está a la vista y no hay nada que abrir.
  const agregarAlChat = useCallback(
    (a: ChatAttachment) => {
      chat.agregarAdjunto(a);
      if (!chat.abierto && chat.vista === 'panel') chat.abrirChat();
    },
    [chat.agregarAdjunto, chat.abierto, chat.vista, chat.abrirChat],
  );

  // ── Verificación de calidad (ADR-2026-09-20-la-verificacion-de-calidad-llega-al-lienzo).
  // El mismo hook y la misma capa que la columna de resultados. Sólo en el lienzo del
  // Agente (con una barra prestada —el modo Diseño— no corresponde). Lo regenerado vuelve
  // por `alDelegar`: aterriza debajo de la foto que se rehízo, como un carrusel.
  const verificacionActiva = useVerificacionCalidad() && !Barra;
  const analisisAutomatico = useAnalisisAutomatico();
  const idsImagenVisibles = useMemo(() => idsVerificables(nodos), [nodos]);
  const tituloPorImagen = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nodos) if (n.data.imageId) m.set(n.data.imageId, n.data.titulo);
    return m;
  }, [nodos]);
  const tituloDe = useCallback((id: string) => tituloPorImagen.get(id) ?? '', [tituloPorImagen]);
  // Las tandas que se vieron volar en ESTE chat y esta sesión: lo único que el análisis
  // automático dispara. Al cambiar de chat se olvida; sus fotos viejas no se analizan solas.
  const chatIdActual = chat.motor.getChatId();
  const [tandasDeSesion, setTandasDeSesion] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    setTandasDeSesion(new Set());
  }, [chatIdActual]);
  useEffect(() => {
    const enVuelo = tandasEnVuelo(tandas);
    if (enVuelo.some((id) => !tandasDeSesion.has(id))) {
      setTandasDeSesion((prev) => new Set([...prev, ...enVuelo]));
    }
  }, [tandas, tandasDeSesion]);
  const idsDeTandas = useMemo(() => idsDeTandasDeSesion(tandas, tandasDeSesion), [tandas, tandasDeSesion]);
  const verificacion = useVerificacion({
    activa: verificacionActiva,
    companyId,
    idsVisibles: idsImagenVisibles,
    tituloDe,
    onGeneracionDelegada: alDelegar,
    automatico: analisisAutomatico,
    idsDeTandas,
  });

  const valor = useMemo<ValorLienzo>(
    () => ({
      companyId,
      brandId: brandId ?? null,
      tiendaConectada,
      miniaturas,
      seleccionadas,
      seleccionAccionable,
      agregarAdjunto: alAgregar ? alAgregar.agregar : agregarAlChat,
      ...(alAgregar ? { rotuloAgregar: alAgregar.rotulo } : {}),
      abrir,
      abrirHerramienta,
      avisarError: setError,
      reportarRatio,
      resolverOpciones,
      aplicarOpciones,
      ordenarSeleccion,
      ...(verificacionActiva ? { verificacion } : {}),
      ...(Barra ? { Barra } : {}),
      ...(Adorno ? { Adorno } : {}),
      ...(alTocarCelda ? { alTocarCelda } : {}),
      ...(sinPie ? { sinPie } : {}),
      ...(Capa ? { Capa } : {}),
    }),
    [
      companyId,
      brandId,
      tiendaConectada,
      miniaturas,
      seleccionadas,
      seleccionAccionable,
      agregarAlChat,
      alAgregar,
      abrir,
      abrirHerramienta,
      reportarRatio,
      resolverOpciones,
      aplicarOpciones,
      ordenarSeleccion,
      verificacionActiva,
      verificacion,
      Barra,
      Adorno,
      alTocarCelda,
      sinPie,
      Capa,
    ],
  );

  return (
    <LienzoContext.Provider value={valor}>
      <ComentariosContext.Provider value={comentariosCtx}>
      <div
        className={`relative h-full min-w-0 transition-opacity duration-300 ease-out ${
          contenidoVisible ? 'opacity-100' : 'opacity-0'
        } ${className}`}
        style={{ width: ancho }}
      >
        <ReactFlow
          // `flujos-tool-*`: los cursores por herramienta de `flujos.css` (y los de
          // `lienzo.css`, que pisan con la flecha y la manito de Atlas).
          className={`lienzo-agente flujos-tool-${herramienta} ${ordenando ? 'lienzo-ordenando' : ''}`}
          nodes={nodes}
          edges={[]}
          nodeTypes={NODE_TYPES}
          onNodesChange={onNodesChange}
          onNodeDragStop={onNodeDragStop}
          onSelectionChange={onSelectionChange}
          onPaneClick={onPaneClick}
          // Con Texto/Nota/Comentar activos, tocar una foto también crea ahí (como Flujos).
          onNodeClick={(e) => {
            if (herramienta === 'texto' || herramienta === 'nota' || herramienta === 'comentar') onPaneClick(e);
          }}
          onBeforeDelete={onBeforeDelete}
          panOnScroll
          zoomOnScroll={false}
          zoomActivationKeyCode={['Meta', 'Control']}
          selectionOnDrag={herramienta === 'seleccionar'}
          panOnDrag={herramienta === 'mano' ? true : [1, 2]}
          nodesDraggable={herramienta === 'seleccionar'}
          selectionMode={SelectionMode.Partial}
          nodesConnectable={false}
          nodesFocusable={false}
          elementsSelectable={herramienta !== 'dibujar'}
          paneClickDistance={8}
          nodeClickDistance={8}
          nodeDragThreshold={5}
          minZoom={0.2}
          maxZoom={2}
          zoomOnDoubleClick={false}
          deleteKeyCode={['Backspace', 'Delete']}
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={22} size={1.6} />
          <Controls position="bottom-left" showInteractive={false} />
          <CapaDibujo
            trazos={trazos}
            activa={herramienta === 'dibujar'}
            estado={estadoDibujo}
            onEstado={(patch) => setEstadoDibujo((e) => ({ ...e, ...patch }))}
            usuario={usuario}
            onAgregar={(t) => setTrazos((prev) => [...prev, t])}
            onBorrarVarios={(ids) => setTrazos((prev) => prev.filter((t) => !ids.includes(t.id)))}
            onSalir={() => cambiarHerramienta('seleccionar')}
          />
          <BarraHerramientas
            activa={herramienta}
            onCambiar={cambiarHerramienta}
            ayudaAbierta={ayudaAbierta}
            onAyuda={setAyudaAbierta}
            atajos={ATAJOS_DEL_LIENZO}
            reemplazo={herramientaDelModo}
            orientacion={barra === 'derecha' ? 'vertical' : 'horizontal'}
          />
          <BarraDeSeleccion />
          {Capa && <Capa />}
        </ReactFlow>
        {/* El filtro gooey del brote de Info (`PieDeCelda`): blur + umbral de alpha, con el
            gráfico original compuesto encima para que el texto quede nítido. Definido una
            vez acá; se aplica por id desde `lienzo.css`. */}
        <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
          <defs>
            <filter id="lienzo-goo">
              <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
              <feColorMatrix
                in="blur"
                mode="matrix"
                values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 20 -9"
                result="goo"
              />
              <feComposite in="SourceGraphic" in2="goo" operator="atop" />
            </filter>
          </defs>
        </svg>
        {chooserElement}
        {/* Los paneles de las herramientas de la barra (Carrusel, Animar, Mejorar,
            Expansión, Recortar, Marca de agua, Descripción), sin pasar por el visor. */}
        <PanelDeHerramienta
          pedido={pedido}
          onCerrar={cerrarHerramienta}
          onGeneracion={alDelegar}
          onImagenNueva={alExpandir}
          onError={setError}
        />
        {/* Sin sistema de toast: el error de una acción se muestra portaleado al body y
            se va al tocarlo. Nunca un catch mudo. */}
        {error &&
          createPortal(
            <button
              type="button"
              onClick={() => setError(null)}
              className="fixed bottom-6 left-1/2 z-[250] -translate-x-1/2 px-4"
            >
              <span className="flex items-center gap-2 rounded-2xl bg-brand-black py-3 px-5 text-sm text-white shadow-xl">
                <AlertCircle className="h-4 w-4 shrink-0 text-red-400" />
                {error}
              </span>
            </button>,
            document.body,
          )}
        {/* El visor prestado del historial: se monta acá pero su lightbox se portaliza
            solo al body. Trae Animar, Carrusel, Mejorar resolución, Expansión, Recortar,
            Marca de agua y Descripción. Carrusel y Animar le entregan la generación al
            lienzo y se cierran; la Expansión avisa la imagen nueva y sigue abierta. */}
        {abierta && (
          <GeneratedImagesHistory
            soloVisor={{
              imageId: abierta.imageId,
              accionInicial: abierta.accion,
              filaInicial: abierta.fila,
              orden: ordenDelVisor,
              ordenCerrado: true,
              filaDe: filaDeCelda,
              brandId: brandId ?? null,
              onCerrar: () => setAbierta(null),
              onError: () => setAbierta(null),
              onGeneracionDelegada: alDelegar,
              onImagenNueva: alExpandir,
            }}
          />
        )}
      </div>
      </ComentariosContext.Provider>
    </LienzoContext.Provider>
  );
};

export const LienzoDelAgente: React.FC<Props> = (props) => (
  <ReactFlowProvider>
    <Lienzo {...props} />
  </ReactFlowProvider>
);

export default LienzoDelAgente;
